import PlusCourseCard from '@comp/PlusCourseCard/PlusCourseCard';
import styled from '@emotion/styled';
import useWindowSize from '@hooks/useWindowSize';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import { processCourse } from '@api/processors/course';
import usePlatformGroupAnalytics from '@cont/BatchGroup/usePlatformGroupAnalytics';
import { SEE_ALL_CLICKED } from '@constants/segment';
import { getBusinessPlatform, getHref } from '@cont/BatchGroup/utils';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import { MAXIMUM_CLASSES_TO_SHOW_WITHOUT_SEE_ALL } from '@cont/BatchGroup/constants';
import CardCarousel from './CardCarousel';

const SecWrapper = styled.section`
  position: relative;
  margin: ${Spacings.SPACING_30B} auto ${Spacings.SPACING_30B};
  width: 1376px;
  overflow: hidden;
  ${mediaQuery.uptoTablet} {
    width: 728px;
    margin-top: ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_14B};
    margin-bottom: -${Spacings.SPACING_4B};
    width: 100%;
  }
`;

export const CaraouselInfo = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_10B};
  align-items: center;
  padding: ${Spacings.SPACING_0B};
  grid-column: span 18;
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;
export const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const ClassCard = styled(PlusCourseCard)`
  width: 272px;
  margin-right: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  .batch-thumbnail {
    width: 100%;
    height: 152px;
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_0B};
    ${mediaQuery.uptoMobile} {
      width: ${({ $hasSingleCard }) => ($hasSingleCard ? '330px' : '272px')};
    }
  }
`;

const SingleClassCard = styled(ClassCard)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const Wrapper = styled.div`
  padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B} ${Spacings.SPACING_10B}
    ${Spacings.SPACING_4B};
`;

const StyH3 = styled(H3)`
  font-size: ${Fonts.H2};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H4};
  }
`;

const SubLabel = styled(P1)`
  font-size: ${Fonts.LARGE};
  margin-top: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const PFTrialClasses = ({ block, href, as, goalUID, goalDetails }) => {
  const { isTablet, isMobile } = useWindowSize();
  const { data } = block;
  const label = block?.extraBlockInfo?.header || 'Trial classes from batches';
  const subLabel = block?.extraBlockInfo?.description;
  const hasTrailClasses = !!data?.length;
  const sendAnalyticsToSegment = usePlatformGroupAnalytics(goalUID);
  const formattedTrialClassData = data?.map?.((trialClass) =>
    processCourse(trialClass)
  );
  const handleSeeAll = () => {
    sendAnalyticsToSegment(SEE_ALL_CLICKED, {
      last_primary_source_section: 'Home',
      last_primary_source: 'Watch Free Classes',
      current_screen: 'Home',
      business_platform: getBusinessPlatform(goalDetails),
      goal_name: goalDetails.name
    });
  };
  const handleOnClickTrailClass = () => {};

  const showSingleEntity = formattedTrialClassData?.length === 1 && isMobile;
  const { updatedHref, updatedAs } = getHref(
    href,
    as,
    isMobile,
    formattedTrialClassData,
    MAXIMUM_CLASSES_TO_SHOW_WITHOUT_SEE_ALL
  );

  return (
    <SecWrapper className="platform-batches-section">
      {showSingleEntity ? (
        <TrailClass
          label={label}
          subLabel={subLabel}
          trialClass={formattedTrialClassData[0]}
          onClick={handleOnClickTrailClass}
        />
      ) : (
        <CardCarousel
          label={label}
          subLabel={subLabel}
          step={hasTrailClasses && !(isTablet || isMobile) ? 4 : 2}
          href={updatedHref}
          as={updatedAs}
          handleSeeAll={handleSeeAll}
          rightShadowDiv={false}
        >
          {formattedTrialClassData?.map((trialClass) => (
            <ClassCard
              key={trialClass.uid}
              course={trialClass}
              disabledTopicGroup
              hideLessonCount
              onCardClick={handleOnClickTrailClass}
            />
          ))}
        </CardCarousel>
      )}
    </SecWrapper>
  );
};
export default PFTrialClasses;

const TrailClass = ({ label, trialClass, subLabel, onClick }) => {
  return (
    <>
      <Wrapper>
        {label ? (
          <CaraouselInfo>
            <Column>
              <StyH3>{label}</StyH3>
              {subLabel && <SubLabel>{subLabel}</SubLabel>}
            </Column>
          </CaraouselInfo>
        ) : null}
        <SingleClassCard
          key={trialClass.uid}
          course={trialClass}
          disabledTopicGroup
          hideLessonCount
          onCardClick={onClick}
        />
      </Wrapper>
    </>
  );
};
